import type { Column } from '@talos/kyoko';
import { Box, setAlpha, useSecuritiesContext } from '@talos/kyoko';
import type { ICellRendererParams, ITooltipParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { createColorBarCellRenderer } from '../../../components/ColorBar';
import type { PublicMarketRowData, PublicMarketsNumericFields } from './types';
import { calculatePublicMarketsMaxValues } from './utils';

interface PublicMarketsColumnConfig {
  field: keyof PublicMarketsNumericFields;
  title: string;
  unit?: string;
  transformValue?: (value: number) => number;
}

const PUBLIC_MARKETS_NUMERIC_COLUMNS: PublicMarketsColumnConfig[] = [
  { field: 'volumeHorizon', title: 'VOLUME-HORIZON' },
  { field: 'adv', title: 'ADV' },
  { field: 'advPct', title: 'ADV (%)', unit: '%', transformValue: v => v * 100 },
  { field: 'spread', title: 'SPREAD', unit: 'BPS' },
  { field: 'spreadDaily', title: 'SPREAD (DAILY)', unit: 'BPS' },
  { field: 'volatility', title: 'VOLATILITY', unit: 'BPS' },
  { field: 'volatilityDaily', title: 'VOLATILITY (DAILY)', unit: 'BPS' },
];

interface UsePublicMarketsColumnsProps {
  rows: PublicMarketRowData[];
}

/**
 * Returns AG Grid column definitions for the Public Markets table.
 * Includes a frozen market name column plus numeric columns (volume, ADV, spread,
 * volatility) rendered as color bars. Max values are derived from the row data
 * to scale bar widths so the largest value in each column fills the bar.
 *
 * @param rows - Public market row data used to compute max values for bar scaling
 * @returns Column definitions for the Public Markets blotter
 */
export function usePublicMarketsColumns({ rows }: UsePublicMarketsColumnsProps) {
  const theme = useTheme();
  const maxValues = useMemo(() => calculatePublicMarketsMaxValues(rows), [rows]);
  const { securitiesBySymbol } = useSecuritiesContext();

  const marketsWithMultipleSymbols = useMemo(() => {
    const seen = new Set<string>();
    const result = new Set<string>();
    for (const row of rows) {
      if (seen.has(row.marketName)) {
        result.add(row.marketName);
      }
      seen.add(row.marketName);
    }
    return result;
  }, [rows]);

  const publicMarketsColors = useMemo<Record<keyof PublicMarketsNumericFields, string>>(
    () => ({
      // TODO: The Figma has colors not yet available in the design system. These are rough estimates. Update once they are.
      // https://www.figma.com/design/XVedSmdWixgRwBZ9DYUNx8/Pre-Trade?node-id=203-26253&t=ALs4nVEkFX6akib9-4
      volumeHorizon: setAlpha(0.7, theme.colors.orange.lighten),
      adv: setAlpha(0.7, theme.colors.blue.default),
      advPct: setAlpha(0.7, theme.colors.blue.default),
      spread: setAlpha(0.7, theme.colors.purple.default),
      spreadDaily: setAlpha(0.7, theme.colors.purple.default),
      volatility: setAlpha(0.7, theme.colors.red.lighten),
      volatilityDaily: setAlpha(0.7, theme.colors.red.lighten),
    }),
    [theme.colors.blue.default, theme.colors.orange.lighten, theme.colors.purple.default, theme.colors.red.lighten]
  );

  return useMemo(() => {
    const columns: Column[] = [
      {
        type: 'custom',
        field: 'marketName',
        title: 'MARKET NAME',
        width: 200,
        frozen: true,
        suppressHeaderMenuButton: true,
        params: {
          tooltipValueGetter: (params: ITooltipParams<PublicMarketRowData>) => {
            const value = params.value as string;
            if (!value) {
              return undefined;
            }
            const displayName = params.context?.current?.marketDisplayNameByName?.get(value) ?? value;
            const symbol = params.data?.symbol;
            const security = symbol ? (securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol) : undefined;
            return `${displayName} (${security})`;
          },
          cellRenderer: (params: ICellRendererParams<PublicMarketRowData>) => {
            const value = params.value as string;
            if (!value) {
              return null;
            }
            const displayName = params.context?.current?.marketDisplayNameByName?.get(value) ?? value;
            const symbol = params.data?.symbol;
            const security = symbol ? (securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol) : undefined;
            if (security && marketsWithMultipleSymbols.has(value)) {
              return (
                <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  {displayName} ({security})
                </Box>
              );
            }
            return (
              <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                {displayName}
              </Box>
            );
          },
        },
      },
      ...PUBLIC_MARKETS_NUMERIC_COLUMNS.map(({ field, title, unit, transformValue }) => {
        const cellRenderer = createColorBarCellRenderer<PublicMarketRowData>({
          field,
          color: publicMarketsColors[field],
          maxValue: maxValues[field],
          ...(unit && {
            unit,
            unitColor: 'colorTextDefault',
          }),
          ...(transformValue && { transformValue }),
        });
        return {
          type: 'custom' as const,
          field,
          title,
          flex: 1,
          suppressHeaderMenuButton: true,
          params: {
            headerClass: 'ag-right-aligned-header',
            cellClass: 'ag-right-aligned-cell',
            cellRenderer,
          },
        };
      }),
    ];
    return columns;
  }, [maxValues, publicMarketsColors, marketsWithMultipleSymbols, securitiesBySymbol]);
}
